import React, { useState, useEffect } from 'react';
import { weeksAPI, gamesAPI, seasonsAPI, handleApiError, adminAPI, selectionsAPI, } from '../services/api';
import WinnerCelebration from './WinnerCelebration';
import HistoricalSeasons from './HistoricalSeasons';

function AdminGames() {
  const [remainingWeeks, setRemainingWeeks] = useState([]);
  const [expiredWeeks, setExpiredWeeks] = useState([]);
  const [establishing, setEstablishing] = useState(false);
  const [cfbdStatus, setCfbdStatus] = useState(null); // { season, weeks, games }
  const [currentWeek, setCurrentWeek] = useState(null);
  const [games, setGames] = useState([]);
  const [selectedGameIDs, setSelectedGameIDs] = useState([]); // From server
  const [currentSelectedGameIDs, setCurrentSelectedGameIDs] = useState([]); // Working copy
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [spreadFilter, setSpreadFilter] = useState(0);
  
  // Celebration and history modals
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);
  const [showHistoricalSeasons, setShowHistoricalSeasons] = useState(false);
  
  // Form states
  const [showAddGameForm, setShowAddGameForm] = useState(false);
  const [newGame, setNewGame] = useState({
    homeTeam: "",
    awayTeam: "",
    gameTime: "",
  });

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadWeeks(), loadCfbdStatus()]);
    } catch (error) {
      setError("Failed to load initial data");
    } finally {
      setLoading(false);
    }
  };

  const loadCfbdStatus = async () => {
    try {
      const response = await adminAPI.getCFBDStatus();
      setCfbdStatus(response.data);
    } catch (error) {
      // Non-fatal -- the button just falls back to "Establish".
      console.warn("Failed to load CFBD status:", error);
      setCfbdStatus(null);
    }
  };

  const loadWeeks = async () => {
    try {
      const response = await weeksAPI.getAll();
      const allWeeks = Array.isArray(response.data) ? response.data : [];

      const activeWeek = allWeeks.find((w) => w.is_active);
      const currentSeason = activeWeek
        ? activeWeek.season
        : Math.max(...allWeeks.map((w) => w.season), 0);
      const seasonWeeks = allWeeks
        .filter((w) => w.season === currentSeason)
        .sort((a, b) => a.week_number - b.week_number);

      const pivot = activeWeek ? activeWeek.week_number : 0;
      setExpiredWeeks(seasonWeeks.filter((w) => w.week_number < pivot));
      setRemainingWeeks(seasonWeeks.filter((w) => w.week_number >= pivot));

      // Land on the active week so games show without an extra click.
      if (activeWeek && !currentWeek) {
        await switchToWeek(activeWeek);
      }
    } catch (error) {
      setError(handleApiError(error, "Failed to load weeks"));
    }
  };

  // "Establish Season" the first time, "Refresh from CFBD" afterwards -- both
  // hit the same idempotent endpoint, which upserts weeks, games and spreads.
  const seasonEstablished = (cfbdStatus?.games ?? 0) > 0;

  const handleSyncSeason = async () => {
    try {
      setEstablishing(true);
      clearMessages();
      const season = cfbdStatus?.season ?? new Date().getFullYear();
      const res = await adminAPI.establishSeason(season);
      const d = res?.data ?? {};
      setSuccess(
        `Season ${season} synced from CFBD: ${d.weeksFromCFBD ?? 0} weeks, ` +
          `${d.insertedGames ?? 0} new / ${d.updatedGames ?? 0} updated games, ` +
          `${d.gamesWithSpread ?? 0} with spreads.`
      );
      await Promise.all([loadWeeks(), loadCfbdStatus()]);
      if (currentWeek) await loadGamesForWeek(currentWeek.id);
    } catch (error) {
      setError(handleApiError(error, "Failed to sync season"));
    } finally {
      setEstablishing(false);
    }
  };

  /*
  const loadActiveWeek = async () => {
    try {
      const response = await weeksAPI.getActive();
      const activeWeek = response.data;
      setCurrentWeek(activeWeek);
      await loadGamesForWeek(activeWeek.id);
    } catch (error) {
      // If no active week exists, create one
      if (error.response?.status === 404) {
        await createFirstWeek();
      } else {
        setError(handleApiError(error, 'Failed to load active week'));
      }
    }
  };

  
  const createFirstWeek = async () => {
    try {
      const response = await weeksAPI.create({
        week_number: 1,
        season: new Date().getFullYear(),
        is_active: true
      });
      const newWeek = response.data;
      setCurrentWeek(newWeek);
      setWeeks([newWeek]);
      setGames([]);
      setSuccess('Created Week 1 for the current season');
    } catch (error) {
      setError(handleApiError(error, 'Failed to create first week'));
    }
  };
  */

  const loadGamesForWeek = async (weekId) => {
    try {
      const response = await gamesAPI.getAll(weekId);
      const sortedGames = (response.data || []).sort(
        (a, b) => new Date(a.game_time) - new Date(b.game_time)
      );
      setGames(sortedGames);
    } catch (error) {
      setError(handleApiError(error, "Failed to load games"));
    }
  };

  const loadSelectedGames = async (weekId) => {
    try {
      const response = await selectionsAPI.getSelectedGames(weekId);
      const gameIDs = response?.data || [];
      setSelectedGameIDs(gameIDs);
      setCurrentSelectedGameIDs(gameIDs); // Initialize working copy
    } catch (error) {
      // Don't show error for selected games, just log it
      console.warn("Failed to load selected games:", error);
      setSelectedGameIDs([]);
      setCurrentSelectedGameIDs([]);
    }
  };

  const switchToWeek = async (week) => {
    try {
      setCurrentWeek(week);
      await Promise.all([
        loadGamesForWeek(week.id),
        loadSelectedGames(week.id),
      ]);
      clearMessages();
    } catch (error) {
      setError(handleApiError(error, "Failed to switch week"));
    }
  };

  const addGame = async () => {
    if (!newGame.homeTeam.trim() || !newGame.awayTeam.trim()) {
      setError("Both home and away teams are required");
      return;
    }

    try {
      const gameData = {
        week_id: currentWeek.id,
        home_team: newGame.homeTeam.trim(),
        away_team: newGame.awayTeam.trim(),
        game_time: newGame.gameTime || new Date().toISOString(),
        winner: null,
        is_complete: false,
      };

      const response = await gamesAPI.create(gameData);
      const updatedGames = [...games, response.data].sort(
        (a, b) => new Date(a.game_time) - new Date(b.game_time)
      );
      setGames(updatedGames);
      setNewGame({ homeTeam: "", awayTeam: "", gameTime: "" });
      setShowAddGameForm(false);
      setSuccess(`Game added: ${newGame.awayTeam} @ ${newGame.homeTeam}`);
      clearMessages(3000);
    } catch (error) {
      setError(handleApiError(error, "Failed to add game"));
    }
  };

  const markGameWinner = async (gameId, winner) => {
    try {
      const game = games.find((g) => g.id === gameId);
      const updatedGame = {
        ...game,
        winner: winner,
        is_complete: true,
      };

      const response = await gamesAPI.update(gameId, updatedGame);
      const updatedGames = games
        .map((g) => (g.id === gameId ? response.data : g))
        .sort((a, b) => new Date(a.game_time) - new Date(b.game_time));
      setGames(updatedGames);

      setSuccess(`Game completed: ${winner} wins`);

      // Check if week is complete and advance automatically
      const completedGames = updatedGames.filter((g) => g.is_complete).length;
      if (completedGames === updatedGames.length && updatedGames.length === 6) {
        //await checkAndAdvanceWeek();
      }

      clearMessages(3000);
    } catch (error) {
      setError(handleApiError(error, "Failed to mark winner"));
    }
  };

  /*
  const checkAndAdvanceWeek = async () => {
    try {
      const response = await weeksAPI.checkAndAdvance();
      const result = response.data;

      if (result.week_advanced) {
        setSuccess(
          `Week ${result.previous_week.week_number} completed! Advanced to Week ${result.new_active_week.week_number}`
        );
        setCurrentWeek(result.new_active_week);
        setGames([]);
        await loadWeeks(); // Reload weeks to include new week
      }
    } catch (error) {
      setError(handleApiError(error, "Failed to advance week"));
    }
  };
  */

  const handleSelectGame = (gameId) => {
    if (currentSelectedGameIDs.length >= 6) {
      setError("Maximum of 6 games can be selected");
      clearMessages(3000);
      return;
    }
    setCurrentSelectedGameIDs([...currentSelectedGameIDs, gameId]);
  };

  const handleDeselectGame = (gameId) => {
    setCurrentSelectedGameIDs(
      currentSelectedGameIDs.filter((id) => id !== gameId)
    );
  };

  const handleSaveSelectedGames = async () => {
    if (!currentWeek) return;

    try {
      await selectionsAPI.saveSelectedGames(
        currentWeek.id,
        currentSelectedGameIDs
      );
      setSelectedGameIDs(currentSelectedGameIDs); // Update server state
      setSuccess("Selected games saved successfully");
      clearMessages(3000);
    } catch (error) {
      setError(handleApiError(error, "Failed to save selected games"));
      clearMessages(3000);
    }
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (selectedGameIDs.length !== currentSelectedGameIDs.length) {
      return true;
    }
    const sortedSelected = [...selectedGameIDs].sort((a, b) => a - b);
    const sortedCurrent = [...currentSelectedGameIDs].sort((a, b) => a - b);
    return JSON.stringify(sortedSelected) !== JSON.stringify(sortedCurrent);
  }
  
  const archiveSeason = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to archive the current season? This will create a new season and cannot be undone.'
    );
    
    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await seasonsAPI.archiveSeason();
      const data = response.data;
      
      // Show celebration modal
      setCelebrationData({
        winner: data.winner,
        season: data.archived_season,
        totalWeeks: data.total_weeks,
      });
      setShowCelebration(true);
      
      // Store the new week data to load after celebration
      setCurrentWeek(data.new_active_week);
      setGames([]);
      
    } catch (error) {
      setError(handleApiError(error, 'Failed to archive season'));
      setLoading(false);
    }
  };

  const handleCelebrationClose = async () => {
    setShowCelebration(false);
    setCelebrationData(null);
    
    // Reload all data
    await loadInitialData();
    setSuccess('New season started! Ready for Week 1.');
    clearMessages(5000);
  };

  const deleteGame = async (gameId) => {
    if (!window.confirm('Are you sure you want to delete this game?')) {
      return;
    }

    try {
      await gamesAPI.delete(gameId);
      setGames(games.filter(g => g.id !== gameId));
      setSuccess('Game deleted successfully');
      clearMessages(3000);
    } catch (error) {
      setError(handleApiError(error, 'Failed to delete game'));
    }
  };

  const clearMessages = (timeout = 0) => {
    if (timeout > 0) {
      setTimeout(() => {
        setError("");
        setSuccess("");
      }, timeout);
    } else {
      setError("");
      setSuccess("");
    }
  };

  const formatGameTime = (gameTime) => {
    return new Date(gameTime).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return <div className="loading">Loading admin panel...</div>;
  }

  return (
    <div className="admin-games">
      {/* Celebration Modal */}
      {showCelebration && celebrationData && (
        <WinnerCelebration
          winner={celebrationData.winner}
          season={celebrationData.season}
          totalWeeks={celebrationData.totalWeeks}
          onClose={handleCelebrationClose}
        />
      )}

      {/* Historical Seasons Modal */}
      {showHistoricalSeasons && (
        <HistoricalSeasons onClose={() => setShowHistoricalSeasons(false)} />
      )}

      <div className="admin-header">
        <h2>Admin - Manage Games</h2>
        {currentWeek && (
          <h3>
            Week {currentWeek.week_number}, {currentWeek.season} Season
            {/*{currentWeek.is_active && <span className="active-badge"> (Active)</span>}*/}
          </h3>
        )}
      </div>

      {/* Admin Action Buttons */}
      <div className="admin-actions">
        <button
          onClick={() => setShowHistoricalSeasons(true)}
          className="btn-secondary"
        >
          📚 View Historical Seasons
        </button>
        <button
          onClick={archiveSeason}
          className="btn-archive"
          disabled={loading}
        >
          🏆 Archive Season
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError("")} className="alert-close">
            &times;
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess("")} className="alert-close">
            &times;
          </button>
        </div>
      )}

      {/* Week Navigation */}
      <div className="week-navigation">
        <h4>Week Navigation</h4>

        {/* Remaining Weeks */}
        {remainingWeeks.length > 0 && (
          <div className="week-section">
            <h5 className="week-section-header">Remaining Weeks</h5>
            <div className="week-tabs">
              {remainingWeeks.map((week) => (
                <button
                  key={week.id}
                  onClick={() => switchToWeek(week)}
                  className={`week-tab ${
                    currentWeek?.id === week.id ? "active" : ""
                  }`}
                >
                  Week {week.week_number}
                  {/*{week.is_active && <span className="active-indicator">●</span>}*/}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Expired Weeks */}
        {expiredWeeks.length > 0 && (
          <div className="week-section">
            <h5 className="week-section-header">Expired Weeks</h5>
            <div className="week-tabs">
              {expiredWeeks.map((week) => (
                <button
                  key={week.id}
                  onClick={() => switchToWeek(week)}
                  className={`week-tab expired ${
                    currentWeek?.id === week.id ? "active" : ""
                  }`}
                >
                  Week {week.week_number}
                  {/*{week.is_active && <span className="active-indicator">●</span>}*/}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {!seasonEstablished && (
        <div className="establish-season-prompt">
          <p>
            No games have been imported for the {cfbdStatus?.season ?? new Date().getFullYear()} season yet.
          </p>
          <button
            onClick={handleSyncSeason}
            className="btn-primary"
            disabled={establishing}
          >
            {establishing
              ? "Establishing…"
              : `Establish Season ${cfbdStatus?.season ?? new Date().getFullYear()}`}
          </button>
        </div>
      )}

      {currentWeek && (
        <div className="week-content">
          {/* Game Management */}
          <div className="games-section">
            <div className="section-header">
              <h4>Games ({currentSelectedGameIDs.length}/6)</h4>
              <div className="section-header-actions">
                <div className="spread-filter">
                  <label htmlFor="spread-filter">Spread under</label>
                  <input
                    id="spread-filter"
                    type="number"
                    min="0"
                    step="0.5"
                    value={spreadFilter}
                    onChange={(e) =>
                      setSpreadFilter(parseFloat(e.target.value) || 0)
                    }
                  />
                </div>
                <button
                  onClick={handleSyncSeason}
                  className="btn-secondary"
                  disabled={establishing}
                  title="Re-pull the schedule, scores and spreads from CollegeFootballData for every week this season"
                >
                  {establishing ? "Refreshing…" : "🔄 Refresh games from CFBD"}
                </button>
                {currentSelectedGameIDs.length < 6 && (
                  <button
                    onClick={() => setShowAddGameForm(!showAddGameForm)}
                    className="btn-primary"
                  >
                    {showAddGameForm ? "Cancel" : "Add Game"}
                  </button>
                )}
              </div>
            </div>
            {spreadFilter > 0 && (
              <p className="spread-filter-note">
                Showing games where |spread| &lt; {spreadFilter}
              </p>
            )}

            {/* Add Game Form */}
            {showAddGameForm && (
              <div className="add-game-form">
                <h5>Add New Game</h5>
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Away Team"
                    value={newGame.awayTeam}
                    onChange={(e) =>
                      setNewGame({ ...newGame, awayTeam: e.target.value })
                    }
                    className="team-input"
                  />
                  <span className="vs-text">@</span>
                  <input
                    type="text"
                    placeholder="Home Team"
                    value={newGame.homeTeam}
                    onChange={(e) =>
                      setNewGame({ ...newGame, homeTeam: e.target.value })
                    }
                    className="team-input"
                  />
                  <input
                    type="datetime-local"
                    value={newGame.gameTime}
                    onChange={(e) =>
                      setNewGame({ ...newGame, gameTime: e.target.value })
                    }
                    className="time-input"
                  />
                  <button onClick={addGame} className="btn-success">
                    Add Game
                  </button>
                </div>
              </div>
            )}

            {/* Games List */}
            <div className="games-list">
              {games.length === 0 ? (
                <p className="no-games">
                  No games added yet. Add up to 6 games for this week.
                </p>
              ) : (
                games
                  .filter((game) => {
                    if (spreadFilter === 0) return true;
                    if (game.spread === null || game.spread === undefined)
                      return false;
                    return Math.abs(game.spread) < spreadFilter;
                  })
                  .map((game) => {
                    const isSelected = currentSelectedGameIDs.includes(game.id);
                    const spreadDisplay =
                      game.spread !== null && game.spread !== undefined
                        ? `(${game.spread > 0 ? "+" : ""}${game.spread.toFixed(1)})`
                        : "";

                    // Winner comes straight from CFBD (or a manual override).
                    const winner = game.is_complete ? game.winner : null;

                    // Check if game is in the future
                    const gameTime = new Date(game.game_time);
                    const now = new Date();
                    const isFutureGame = gameTime > now;

                    return (
                      <div
                        key={game.id}
                        className={`game-card ${
                          game.is_complete ? "completed" : "pending"
                        }${isSelected ? " selected" : ""}`}
                      >
                        <div className="game-info">
                          <div className="teams">
                            <span
                              className={`away-team ${
                                winner === game.away_team ? "winner" : ""
                              }`}
                            >
                              {game.away_team}
                            </span>
                            <span className="vs">@</span>
                            <span
                              className={`home-team ${
                                winner === game.home_team ? "winner" : ""
                              }`}
                            >
                              {game.home_team}
                              {spreadDisplay && (
                                <span
                                  style={{
                                    marginLeft: "0.25rem",
                                    color: "#666",
                                    fontSize: "0.9em",
                                  }}
                                >
                                  {spreadDisplay}
                                </span>
                              )}
                            </span>
                            {isSelected && (
                              <span className="selected-badge">Selected</span>
                            )}
                          </div>
                          <div className="game-time">
                            {formatGameTime(game.game_time)}
                          </div>
                        </div>

                        <div className="game-actions">
                          {game.is_complete && winner && (
                            <div className="winner-display">
                              <span className="winner-label">Winner:</span>
                              <span className="winner-team">{winner}</span>
                            </div>
                          )}

                          {!game.is_complete && !isFutureGame && (
                            <div className="winner-picker">
                              <span className="winner-label">Set winner:</span>
                              <button
                                onClick={() =>
                                  markGameWinner(game.id, game.away_team)
                                }
                                className="btn-secondary btn-small"
                              >
                                {game.away_team}
                              </button>
                              <button
                                onClick={() =>
                                  markGameWinner(game.id, game.home_team)
                                }
                                className="btn-secondary btn-small"
                              >
                                {game.home_team}
                              </button>
                            </div>
                          )}

                          {!game.is_complete &&
                            (isSelected ? (
                              <button
                                onClick={() => handleDeselectGame(game.id)}
                                className="btn-warning btn-small"
                                title={
                                  isFutureGame
                                    ? "Cannot deselect future games"
                                    : "Deselect game"
                                }
                                disabled={!isFutureGame}
                              >
                                Deselect
                              </button>
                            ) : (
                              <button
                                onClick={() => handleSelectGame(game.id)}
                                className="btn-primary btn-small"
                                title={
                                  isFutureGame
                                    ? "Cannot select future games"
                                    : !isFutureGame ||
                                      currentSelectedGameIDs.length >= 6
                                    ? "Maximum of 6 games selected"
                                    : "Select game"
                                }
                                disabled={
                                  !isFutureGame ||
                                  currentSelectedGameIDs.length >= 6
                                }
                              >
                                Select
                              </button>
                            ))}

                          <button
                            onClick={() => deleteGame(game.id)}
                            className="btn-danger btn-small"
                            title="Remove this game from the week"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })
              )}
            </div>

            {/* Week Status */}
            {games.length > 0 && (
              <div className="week-status">
                <div className="status-info">
                  <span>
                    Games Complete: {games.filter((g) => g.is_complete).length}/
                    {games.length}
                  </span>
                  {games.length === 6 &&
                    games.filter((g) => g.is_complete).length === 6 && (
                      <span className="week-complete-indicator">
                        ✓ Week Complete - Will advance to next week
                      </span>
                    )}
                </div>
              </div>
            )}

            {/* Save Button - Only show if there are unsaved changes */}
            {hasUnsavedChanges() && (
              <div className="save-section">
                <button
                  onClick={handleSaveSelectedGames}
                  className="btn-success"
                  style={{ width: "100%", padding: "1rem", fontSize: "1.1rem" }}
                >
                  Save Selected Games
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminGames;
